import { c as _c } from "react/compiler-runtime";
import { MemberGroupOtherProjectsNote } from "@components/common/member/member-access-summary";
import { filterProjectsByAccess, hasMixedAccessSources, MemberGroupProjectsHeader } from "@components/common/member/member-group-projects-header";
import { MemberGroupProjectsSkeleton } from "@components/common/skeleton-loading/member-group-projects-skeleton";
import { MemberGroupProjectsTable } from "@components/table/member-group-projects/member-group-projects-table";
import { MemberGroupProjectsHeaders } from "@components/table/member-group-projects/member-group-projects-table-utils";
import { BulkRemoveFromProjects } from "@components/table/member-group-projects/bulk-actions/bulk-remove-from-projects";
import { BulkChangeGroupProjectRole } from "@components/table/member-group-projects/bulk-actions/bulk-change-group-project-role";
import { isGroupRole } from "@custom-types/member-type-guards";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { useMemberGroupDetails } from "@hooks/use-member-group-details";
import { Box } from "@mui-wrapper";
import { companyMemberGroupProjectsSelector } from "@store/company-member-groups/company-member-groups-selector";
import { removeGroup } from "@store/company-member-groups/company-member-groups-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { CommonStyles, DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { UserProfile } from "@utils/track-event/track-event-list";
import { getPrettyName } from "@utils/user-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useCallback, useEffect, useRef, useState } from "react";
/**
 * Renders a table with projects of a group that a member belongs to
 */
export function MemberGroupProjects(t0) {
  const $ = _c(66);
  const {
    group,
    member
  } = t0;
  const dispatch = useAppDispatch();
  const {
    canRemoveMemberFromProject,
    canChangeMemberProjectRole
  } = useHasUserValidRoleCompanyLevel();
  const {
    canAssignProjectRoles
  } = useHasUserValidFeatureCompanyLevel();
  let t1;
  if ($[0] !== group.id) {
    t1 = {
      groupId: group.id
    };
    $[0] = group.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    groupDetails,
    isFetchingGroupDetails,
    removeProjects,
    refetchDetails
  } = useMemberGroupDetails(t1);
  let t2;
  if ($[2] !== group.id) {
    t2 = companyMemberGroupProjectsSelector(group.id);
    $[2] = group.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const projects = useAppSelector(t2);
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const pendingRemovedIds = useRef(t3);
  const [accessFilter, setAccessFilter] = useState("all");
  const {
    trackEvent
  } = useTrackEvent();
  let t4;
  if ($[5] !== dispatch || $[6] !== group.id || $[7] !== group.role || $[8] !== projects.length || $[9] !== removeProjects) {
    t4 = removedProjectIds => {
      const remainingCount = projects.length - removedProjectIds.length;
      const isGroupManager = group.role && isGroupRole(group.role);
      if (remainingCount <= 0 && !isGroupManager) {
        dispatch(removeGroup(group.id));
      } else {
        removeProjects(removedProjectIds);
      }
    };
    $[5] = dispatch;
    $[6] = group.id;
    $[7] = group.role;
    $[8] = projects.length;
    $[9] = removeProjects;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const cleanupAfterRemoval = t4;
  let t5;
  let t6;
  if ($[11] !== cleanupAfterRemoval || $[12] !== shouldShowSuccessDialog) {
    t5 = () => {
      if (pendingRemovedIds.current.length > 0 && !shouldShowSuccessDialog) {
        const ids = pendingRemovedIds.current;
        pendingRemovedIds.current = [];
        cleanupAfterRemoval(ids);
      }
    };
    t6 = [shouldShowSuccessDialog, cleanupAfterRemoval];
    $[11] = cleanupAfterRemoval;
    $[12] = shouldShowSuccessDialog;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useEffect(t5, t6);
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = removedProjectIds_0 => {
      pendingRemovedIds.current = removedProjectIds_0;
    };
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleSuccessfulRemoval = t7;
  if (groupDetails?.projectIds === undefined) {
    if (isFetchingGroupDetails) {
      let t8;
      if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <MemberGroupProjectsSkeleton />;
        $[16] = t8;
      } else {
        t8 = $[16];
      }
      return t8;
    }
    return null;
  }
  if (!projects.length) {
    let t8;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        mb: 2
      };
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {
        display: "flex",
        justifyContent: "center",
        padding: "8px 16px",
        borderBottom: CommonStyles.Borders.divider,
        my: 2,
        color: "text.secondary",
        fontSize: DEFAULT_TEXT_FONT_SIZE
      };
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== member) {
      t10 = getPrettyName(member);
      $[19] = member;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== t10) {
      t11 = <Box component="div" sx={t9}><span><var>{t10}</var> isn't assigned to any project in this group.</span></Box>;
      $[21] = t10;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== group || $[24] !== member) {
      t12 = <MemberGroupOtherProjectsNote member={member} group={group} hasAssignedProjects={false} />;
      $[23] = group;
      $[24] = member;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    let t13;
    if ($[26] !== t11 || $[27] !== t12) {
      t13 = <Box component="div" sx={t8}>{t11}{t12}</Box>;
      $[26] = t11;
      $[27] = t12;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    return t13;
  }
  const canChangeProjectRole = canChangeMemberProjectRole && canAssignProjectRoles;
  const hasBulkActions = canRemoveMemberFromProject || canChangeProjectRole;
  let t8;
  if ($[29] !== canChangeProjectRole || $[30] !== canRemoveMemberFromProject || $[31] !== hasBulkActions || $[32] !== member || $[33] !== projects || $[34] !== refetchDetails) {
    t8 = hasBulkActions ? {
      removeMemberFromProject: canRemoveMemberFromProject ? <BulkRemoveFromProjects member={member} projects={projects} onSuccessfulComplete={handleSuccessfulRemoval} /> : null,
      changeMemberProjectRole: canChangeProjectRole ? <BulkChangeGroupProjectRole member={member} projects={projects} onRoleChanged={refetchDetails} /> : null
    } : undefined;
    $[29] = canChangeProjectRole;
    $[30] = canRemoveMemberFromProject;
    $[31] = hasBulkActions;
    $[32] = member;
    $[33] = projects;
    $[34] = refetchDetails;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  const bulkActionButtons = t8;
  let t9;
  if ($[36] !== accessFilter || $[37] !== projects) {
    t9 = hasMixedAccessSources(projects) ? accessFilter : "all";
    $[36] = accessFilter;
    $[37] = projects;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  const activeFilter = t9;
  let t10;
  if ($[39] !== trackEvent) {
    t10 = function handleFilterChange(filter) {
      trackEvent({
        name: UserProfile.filterGroupProjects,
        props: {
          filter
        }
      });
      setAccessFilter(filter);
    };
    $[39] = trackEvent;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  const handleFilterChange = t10;
  let t11;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      mb: 2
    };
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== activeFilter || $[43] !== handleFilterChange || $[44] !== member || $[45] !== projects) {
    t12 = <MemberGroupProjectsHeader member={member} projects={projects} filter={activeFilter} onFilterChange={handleFilterChange} />;
    $[42] = activeFilter;
    $[43] = handleFilterChange;
    $[44] = member;
    $[45] = projects;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== activeFilter || $[48] !== projects) {
    t13 = filterProjectsByAccess(projects, activeFilter);
    $[47] = activeFilter;
    $[48] = projects;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [MemberGroupProjectsHeaders.thumbnail, MemberGroupProjectsHeaders.name, MemberGroupProjectsHeaders.role, MemberGroupProjectsHeaders.access, MemberGroupProjectsHeaders.options];
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  const t15 = !hasBulkActions;
  let t16;
  if ($[51] !== bulkActionButtons || $[52] !== cleanupAfterRemoval || $[53] !== isFetchingGroupDetails || $[54] !== member || $[55] !== refetchDetails || $[56] !== t13 || $[57] !== t15) {
    t16 = <MemberGroupProjectsTable projects={t13} member={member} onMemberRemoved={cleanupAfterRemoval} onRoleChanged={refetchDetails} isLoading={isFetchingGroupDetails} requiredColumns={t14} bulkActionButtons={bulkActionButtons} shouldHideCheckbox={t15} />;
    $[51] = bulkActionButtons;
    $[52] = cleanupAfterRemoval;
    $[53] = isFetchingGroupDetails;
    $[54] = member;
    $[55] = refetchDetails;
    $[56] = t13;
    $[57] = t15;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== group || $[60] !== member) {
    t17 = <MemberGroupOtherProjectsNote member={member} group={group} hasAssignedProjects={true} />;
    $[59] = group;
    $[60] = member;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== t12 || $[63] !== t16 || $[64] !== t17) {
    t18 = <Box component="div" sx={t11}>{t12}{t16}{t17}</Box>;
    $[62] = t12;
    $[63] = t16;
    $[64] = t17;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  return t18;
}